import { ILocationFilterProps } from 'components/LocationFilterInput/LocationFilterInput.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { DateTime } from 'luxon';
import {
  DashboardPageAvailableShiftsPayload,
  DashboardPageAvailableShiftsPayloadTransformer as DPUSPT,
} from 'pages/DashboardAvailableShiftsPage/DashboardAvailableShiftsPage.types';
import { authLink, createApolloClient, httpLink } from 'providers/ApolloProvider';
import { QUERY_dashboardAvailableShiftsPage } from 'queries';
import { SessionService } from 'services';
import { isOrderStatusInProgress, isOrderStatusUnfulfilled } from 'types/common.types';
import {
  DashboardAvailableShiftsPageQuery,
  Order,
  OrderSortableFieldsEnum,
  SortDirectionEnum,
} from 'types/graphql';
import { compact } from 'utils/compact';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { isDateTimeBeforeNow } from 'utils/time';

const dashboardAvailableShiftsPagePayloadTransformer: DPUSPT = (values) => {
  const { isUserAgencyAndAgencyLocationFilterForcedDisabled, ...filterValues } = values;

  const { timeZone } = getCurrentlySelectedLocation();

  const variables: DashboardPageAvailableShiftsPayload = {
    currentPage: filterValues.currentPage,
    first: filterValues.first,
    last: filterValues.last,
    after: filterValues.after,
    before: filterValues.before,
    filter: {
      forOrders: {
        withCommunity: undefined,
        withStatus: filterValues.shiftStatus,
        withPositions: filterValues.position_id,
        withJaneType: [...filterValues.internalJaneType],
        withStaff: {
          types: [...filterValues.externalJaneType],
          facility: {
            id: Number(filterValues.location.id),
            type: SessionService.assertUserType('B2B') ? 'Community' : 'AgencyCommunity',
          },
        },
        startBetweenTimeRange: {
          from: DateTime.now().setZone(timeZone).startOf('day').toString(),
          to: DateTime.now().setZone(timeZone).plus({ month: 1 }).toString(),
        },
        withinBounds: undefined,
      },
      forJanes: {
        byEmploymentStatus: filterValues.employmentStatus,
        byFullName: filterValues.byFullName,
      },
      forAgencyCommunities: {
        withId: undefined,
      },
    },
    sortOrders: [
      {
        field: OrderSortableFieldsEnum.startAt,
        direction: SortDirectionEnum.asc,
      },
      {
        field: OrderSortableFieldsEnum.positionName,
        direction: SortDirectionEnum.asc,
      },
    ],
  };
  if (!filterValues?.viewAllFacilities) {
    if (SessionService.assertUserType('B2B'))
      variables.filter.forOrders['withCommunity'] = Number(filterValues.location.value);

    if (
      SessionService.assertUserType('Agency') &&
      !isUserAgencyAndAgencyLocationFilterForcedDisabled
    )
      variables.filter.forAgencyCommunities['withId'] = Number(filterValues.location.value);
  }

  if (values.withinBounds) {
    variables.filter.forOrders.withinBounds = {
      northEast: values.withinBounds.northEast,
      southWest: values.withinBounds.southWest,
    };
  }

  return variables;
};

export function dashboardAvailableShiftsPageResponseTransformer(
  data: DashboardAvailableShiftsPageQuery,
): Nullable<DashboardAvailableShiftsPageQuery> {
  if (!data) return null;
  const newData: DashboardAvailableShiftsPageQuery = JSON.parse(JSON.stringify(data));

  if (!newData) return null;
  if (!newData?.fastGroupOrders) return null;
  if (!newData?.fastGroupOrders?.nodes) return null;

  const inProgressData: DashboardAvailableShiftsPageQuery = newData;
  const notInProgressData: DashboardAvailableShiftsPageQuery = JSON.parse(JSON.stringify(data));

  if (!!inProgressData?.fastGroupOrders?.nodes && inProgressData?.fastGroupOrders?.nodes.length)
    inProgressData?.fastGroupOrders?.nodes.forEach((node) => {
      if (node) {
        const isToday = DateTime.fromISO(node.group).day === DateTime.now().day;
        if (isToday) {
          const unfulfilledOrders = [] as Order[];
          (node?.orders as Order[])?.forEach((record: Order, index2: number, arr2: Order[]) => {
            if (
              !isOrderStatusInProgress(record.status.value) &&
              isDateTimeBeforeNow(record.endAt)
            ) {
              delete arr2[index2];
            }

            if (isOrderStatusUnfulfilled(record.status.value)) {
              unfulfilledOrders.push(record);
              delete arr2[index2];
            }
          });

          // @ts-ignore
          node.orders = [...node.orders, ...unfulfilledOrders];
        }
      }
    });

  if (
    !!notInProgressData?.fastGroupOrders?.nodes &&
    notInProgressData?.fastGroupOrders?.nodes.length
  )
    notInProgressData?.fastGroupOrders?.nodes.forEach((node) => {
      if (node) {
        (node?.orders as Order[])?.forEach((record: Order, index2: number, arr2: Order[]) => {
          if (isDateTimeBeforeNow(record.endAt) || isOrderStatusInProgress(record.status.value)) {
            delete arr2[index2];
          }
        });
      }
    });

  let result = {
    ...notInProgressData,
    ...inProgressData,
  };
  let filtered = {
    ...result,
    fastGroupOrders: {
      ...result.fastGroupOrders,
      nodes: result.fastGroupOrders.nodes
        .filter((record) => record.orders.length !== 0)
        .map((record) => {
          const { orders: _orders } = record;

          return { ...record, orders: compact(_orders) };
        }),
    },
  } as DashboardAvailableShiftsPageQuery;

  (filtered.fastGroupOrders.nodes || []).forEach((record, index, arr) => {
    if (record.orders.length === 0) {
      delete arr[index];
    }
  });
  if (filtered.fastGroupOrders.nodes)
    filtered.fastGroupOrders.nodes = filtered.fastGroupOrders.nodes.filter(Boolean);
  return filtered;
}

const client = createApolloClient();

export const dashboardAvailableShiftsPageConfig = ({
  location,
  isUserAgencyAndAgencyLocationFilterForcedDisabled,
}: {
  location: ILocationFilterProps['value'];
  isUserAgencyAndAgencyLocationFilterForcedDisabled: boolean;
}): ISSFBehaviorsOptions<
  DashboardAvailableShiftsPageQuery,
  ReturnType<typeof dashboardAvailableShiftsPageResponseTransformer>
> => {
  client.setLink(authLink.concat(httpLink()));
  return {
    key: 'DASHBOARD_AVAILABLE-SHIFTS_PAGE',
    paginationType: 'InfiniteScroll',
    query: QUERY_dashboardAvailableShiftsPage,
    client,
    responseTransformer: dashboardAvailableShiftsPageResponseTransformer,
    nextFetchPolicy: 'network-only',
    pageSize: 20,
    payloadTransformer: (values) =>
      dashboardAvailableShiftsPagePayloadTransformer({
        ...values,
        isUserAgencyAndAgencyLocationFilterForcedDisabled,
      }),
    schema: {
      position_id: {
        initialValue: [],
        isSessionCached: true,
      },
      shiftStatus: {
        initialValue: ['pending'],
        isSessionCached: true,
      },
      location: {
        initialValue: location,

        isGlobalCached: true,
        isPreservedOnReset: true,
        resetFieldsOnChange: [
          {
            field: 'position_id',
            userTypes: ['Agency'],
          },
        ],
      },
      withinBounds: {
        initialValue: undefined,
        isSessionCached: false,
        isPreservedOnReset: false,
      },
      employmentStatus: {
        initialValue: [],
        isSessionCached: true,
      },
      byFullName: {
        initialValue: '',
        isSessionCached: true,
      },
      internalJaneType: {
        initialValue: [],
        isSessionCached: true,
      },
      externalJaneType: {
        initialValue: [],
        isSessionCached: true,
      },
      viewAllFacilities: {
        initialValue: false,
        isGlobalCached: true,
        isSessionCached: true,
        isPreservedOnReset: true,
      },
    },
  };
};
